import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import { getAiDrinks } from "../../../../../services/Admin/AiDrinksApi";

const AddCustomDrinkTab = ({ 
	open, 
	form, 
	onFormChange, 
	onDrinkSelect 
}) => {
	const [loadingDrinks, setLoadingDrinks] = useState(false);
	const [aiDrinks, setAiDrinks] = useState([]);
	const [selectedDrink, setSelectedDrink] = useState("");

	// Load AI drinks when dialog opens
	useEffect(() => {
		let mounted = true;
		const load = async () => {
			setLoadingDrinks(true);
			try {
				const res = await getAiDrinks();
				if (!mounted) return;
				setAiDrinks(res?.data || []);
			} catch (err) {
				console.error("Failed to load AI drinks:", err);
			} finally {
				if (mounted) setLoadingDrinks(false);
			}
		};

		if (open) load();
		return () => (mounted = false);
	}, [open]);

	// Reset when dialog closes
	useEffect(() => {
		if (!open) {
			setSelectedDrink("");
		}
	}, [open]);

	const handleDrinkChange = (drinkId) => {
		setSelectedDrink(drinkId);
		const drink = aiDrinks.find(d => d.id === drinkId);
		
		// Notify parent
		if (onDrinkSelect) {
			onDrinkSelect(drinkId, drink);
		}

		// Auto-fill price if drink has price
		if (drink?.price) {
			onFormChange("price", drink.price);
		}
	};

	const handleChange = (key) => (e) => {
		onFormChange(key, e.target.value);
	};

	return (
		<Box sx={{ display: "grid", gap: 2, pt: 1 }}>
			{/* AI Drink Selection */}
			<FormControl fullWidth required>
				<InputLabel id="drink-label">المشروب المخصص</InputLabel>
				<Select 
					labelId="drink-label" 
					value={selectedDrink} 
					label="المشروب المخصص" 
					onChange={(e) => handleDrinkChange(e.target.value)}
					disabled={loadingDrinks}
				>
					<MenuItem value="">-- اختر المشروب --</MenuItem>
					{aiDrinks.map((drink) => (
						<MenuItem key={drink.id} value={drink.id}>
							{drink.drink_name || drink.name}
							{drink.temperature && ` (${drink.temperature}°)`}
							{drink.is_approved === 0 && ' ⏳'}
							{drink.is_approved === 1 && ' ✓'}
						</MenuItem>
					))}
				</Select>
				{loadingDrinks && (
					<Typography variant="caption" sx={{ mt: 0.5 }}>
						جاري تحميل المشروبات...
					</Typography>
				)}
			</FormControl>

			{/* Display drink details if selected */}
			{selectedDrink && (() => {
				const drink = aiDrinks.find(d => d.id === selectedDrink);
				if (!drink) return null;

				return (
					<Box sx={{ display: 'grid', gap: 1.5 }}>
						{/* Basic Info */}
						<Box sx={{ p: 2, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.200' }}>
							<Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main' }}>
								معلومات المشروب
							</Typography>
							<Typography variant="body2">
								<strong>الاسم:</strong> {drink.drink_name || drink.name}
							</Typography>
							{drink.category?.name && (
								<Typography variant="body2">
									<strong>الفئة:</strong> {drink.category.name}
								</Typography>
							)}
							{drink.temperature && (
								<Typography variant="body2">
									<strong>درجة الحرارة:</strong> {drink.temperature}°
								</Typography>
							)}
							<Typography variant="body2">
								<strong>الحالة:</strong> {
									drink.is_approved === 1 ? '✓ معتمد' : 
									drink.is_approved === 0 ? '⏳ قيد المراجعة' : 
									'❌ غير معتمد'
								}
							</Typography>
							{drink.description && (
								<Typography variant="body2" sx={{ mt: 0.5 }}>
									<strong>الوصف:</strong> {drink.description}
								</Typography>
							)}
						</Box>

						{/* Materials */}
						{drink.materials && Array.isArray(drink.materials) && drink.materials.length > 0 && (
							<Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
								<Typography variant="subtitle2" sx={{ mb: 1 }}>المكونات والمواد:</Typography>
								{drink.materials.map((mat, idx) => (
									<Box key={idx} sx={{ mb: 0.5, pl: 1, borderLeft: '3px solid', borderColor: 'primary.main' }}>
										<Typography variant="body2">
											• <strong>{mat.material?.name || `المادة #${mat.material_id}`}</strong>
											{' - '}{mat.quantity} {mat.material?.measurment_unit?.abbreviation || 'وحدة'}
										</Typography>
									</Box>
								))}
							</Box>
						)}

						{/* Ingredients String (fallback) */}
						{drink.ingredients && !drink.materials?.length && (
							<Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
								<Typography variant="subtitle2" sx={{ mb: 1 }}>المكونات:</Typography>
								<Typography variant="body2">
									{(() => {
										try {
											const ingredients = JSON.parse(drink.ingredients);
											if (Array.isArray(ingredients)) {
												return ingredients.map((ing, idx) => (
													<div key={idx}>
														• {ing.name || `مادة #${ing.material_id}`} - {ing.amount} {ing.unit}
													</div>
												));
											}
											return drink.ingredients;
										} catch {
											return drink.ingredients;
										}
									})()}
								</Typography>
							</Box>
						)}
					</Box>
				);
			})()}

			<TextField 
				label="الكمية" 
				type="number"
				value={form.quantity} 
				onChange={handleChange("quantity")} 
				fullWidth 
				required
				inputProps={{ min: 1 }}
			/>

			<TextField 
				label="السعر" 
				type="number"
				value={form.price} 
				onChange={handleChange("price")} 
				fullWidth 
				required
				disabled={!!selectedDrink}
				inputProps={{ min: 0, step: 0.01 }}
			/>

			<TextField 
				label="ملاحظات" 
				value={form.note} 
				onChange={handleChange("note")} 
				fullWidth 
				multiline 
				rows={2}
				placeholder="مثال: باردة جداً"
			/>
		</Box>
	);
};

export default AddCustomDrinkTab;
